import React, { useEffect, useState, Suspense, lazy } from 'react';
import { BrowserRouter as Router, Route, Routes, Navigate } from 'react-router-dom'
import './App.css';
import './styles/typography.css';
import 'bootstrap/dist/css/bootstrap.min.css';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import ProtectedRoute from './components/ProtectedRoute';
import fetchInterceptor from './utils/fetchInterceptor';
import SubscriptionRequiredModal from './components/SubscriptionRequiredModal';
import { PageSkeleton } from './components/skeletons';
import TwilioPhoneNumberSettings from './components/TwilioPhoneNumberSettings';
import TwilioService from './services/TwilioService';

// Lazy load CRM pages
const CRMLayout = lazy(() => import('./pages/crm-layout'));

// Lazy load auth pages
const Login = lazy(() => import('./pages/login'));
const ForgotPassword = lazy(() => import('./pages/forgot-password'));
const Profile = lazy(() => import('./pages/profile'));
const AccountCreation = lazy(() => import('./pages/account-creation'));
const AdsDashboard = lazy(() => import('./pages/ads-dashboard'));
const AdsConfig = lazy(() => import('./pages/ads-config'));
const Billing = lazy(() => import('./pages/BillingPage'));

// Lazy load public marketing pages
const MarketingUnsubscribe = lazy(() => import('./pages/marketing-unsubscribe'));
const MarketingResubscribe = lazy(() => import('./pages/marketing-resubscribe'));

// Lazy load public appointment management page
const ManageAppointment = lazy(() => import('./pages/manage-appointment'));
// Lazy load public review page
const Review = lazy(() => import('./pages/review'));

// Lazy load public Android download page
const AndroidDownload = lazy(() => import('./pages/android-download'));

// Inner app component that has access to AuthContext
function AppContent() {
  const [showSubscriptionModal, setShowSubscriptionModal] = useState(false);
  const [subscriptionMessage, setSubscriptionMessage] = useState('');
  const [showTwilioSetupModal, setShowTwilioSetupModal] = useState(false);
  const [twilioCheckComplete, setTwilioCheckComplete] = useState(false);
  const { currentUser } = useAuth();
  const isInTrial = currentUser?.subscription?.isInTrialPeriod || false;

  useEffect(() => {
    // Clear any stale subscription errors on app initialization
    const clearStaleSubscriptionErrors = () => {
      const storedError = sessionStorage.getItem('subscriptionError');
      if (storedError) {
        try {
          const error = JSON.parse(storedError);
          // Clear if error is more than 5 minutes old or if in trial
          if (Date.now() - error.timestamp > 300000 || isInTrial) {
            sessionStorage.removeItem('subscriptionError');
          }
        } catch (e) {
          // If we can't parse it, remove it
          sessionStorage.removeItem('subscriptionError');
        }
      }
    };

    clearStaleSubscriptionErrors();

    // Listen for subscription-required events
    const handleSubscriptionRequired = (event: CustomEvent) => {
      // Don't show modal if in trial period
      if (isInTrial) {
        console.log('🎉 Ignoring subscription modal - user is in trial period');
        return;
      }

      // Don't show modal if Twilio setup modal is open (avoid focus conflicts)
      if (showTwilioSetupModal) {
        console.log('📞 Ignoring subscription modal - Twilio setup in progress');
        return;
      }

      setSubscriptionMessage(event.detail.message || 'An active subscription is required to access this feature');
      setShowSubscriptionModal(true);
    };

    window.addEventListener('subscription-required', handleSubscriptionRequired as EventListener);

    return () => {
      window.removeEventListener('subscription-required', handleSubscriptionRequired as EventListener);
    };
  }, [isInTrial, showTwilioSetupModal])

  // Hide modal if user enters trial period
  useEffect(() => {
    if (isInTrial && showSubscriptionModal) {
      setShowSubscriptionModal(false);
    }
  }, [isInTrial, showSubscriptionModal]);

  // Check for Twilio configuration after login - REQUIRED CHECK
  useEffect(() => {
    const checkTwilioConfig = async () => {
      // Only check if user is logged in and has companyId and roles
      if (!currentUser?.companyId || !currentUser?.roles) {
        return;
      }

      // Only show for admin/super_admin users
      if (!currentUser.roles.includes('admin') && !currentUser.roles.includes('super_admin')) {
        return;
      }

      try {
        const twilioService = new TwilioService();
        const config = await twilioService.getCompanyTwilioConfig();

        // If no active config, show setup modal
        if (!config || config.status !== 'active') {
          console.log('📞 No active Twilio config found - showing setup modal');
          setShowTwilioSetupModal(true);
        }
      } catch (error: any) {
        // Check for TWILIO_NOT_CONFIGURED error code
        if (error.code === 'TWILIO_NOT_CONFIGURED') {
          console.log('📞 TWILIO_NOT_CONFIGURED error - showing setup modal');
          setShowTwilioSetupModal(true);
        } else {
          console.error('Error checking Twilio config:', error);
          // Don't show modal on other errors (network issues, etc.)
        }
      } finally {
        setTwilioCheckComplete(true);
      }
    };

    if (!twilioCheckComplete) {
      checkTwilioConfig();
    }
  }, [currentUser?.companyId, currentUser?.roles, twilioCheckComplete]);

  return (
    <div>
      <SubscriptionRequiredModal
        show={showSubscriptionModal && !isInTrial}
        onHide={() => setShowSubscriptionModal(false)}
        message={subscriptionMessage}
      />
      {showTwilioSetupModal && (
        <TwilioPhoneNumberSettings
          showAsModal={true}
          required={true}
          onConfigured={() => {
            // Only close modal after successful configuration
            setShowTwilioSetupModal(false);
            console.log('✅ Phone number configured - user can now access the app');
          }}
          onClose={() => {
            // Allow user to skip for now
            setShowTwilioSetupModal(false);
            console.log('⏭️ User skipped phone number setup');
          }}
        />
      )}
      <Suspense fallback={<PageSkeleton />}>
        <Routes>
          {/* Default route redirects to login */}
          <Route path="/" element={<Navigate to="/login" replace />} />

          {/* Authentication routes */}
          <Route path="/login" element={<Login />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/create-account" element={<AccountCreation />} />

          {/* Public marketing routes */}
          <Route path="/marketing/unsubscribe" element={<MarketingUnsubscribe />} />
          <Route path="/marketing/resubscribe" element={<MarketingResubscribe />} />

          {/* Public appointment management route */}
          <Route path="/manage/:appointmentId/:token" element={<ManageAppointment />} />
          {/* Public review route */}
          <Route path="/review" element={<Review />} />

          {/* Public Android app download route */}
          <Route path="/android/download" element={<AndroidDownload />} />

          {/* Protected routes */}
          <Route path="/profile" element={
            <ProtectedRoute>
              <Profile />
            </ProtectedRoute>
          } />
          <Route path="/profile/calendar_sync/:provider" element={
            <ProtectedRoute>
              <Profile />
            </ProtectedRoute>
          } />
          <Route path="/billing" element={
            <ProtectedRoute>
              <Billing />
            </ProtectedRoute>
          } />
          <Route path="/ads-dashboard" element={
            <ProtectedRoute>
              <AdsDashboard />
            </ProtectedRoute>
          } />
          <Route path="/ads-config" element={
            <ProtectedRoute>
              <AdsConfig />
            </ProtectedRoute>
          } />

          {/* Default CRM route */}
          <Route path="/crm" element={
            <ProtectedRoute>
              <CRMLayout />
            </ProtectedRoute>
          } />

          {/* CRM routes with settings parameter */}
          <Route path="/crm/:setting/*" element={
            <ProtectedRoute>
              <CRMLayout />
            </ProtectedRoute>
          } />

          {/* Redirect old routes to CRM */}
          <Route path="/chat" element={
            <ProtectedRoute>
              <Navigate to="/crm/chat" replace />
            </ProtectedRoute>
          } />
          <Route path="/documents" element={
            <ProtectedRoute>
              <Navigate to="/crm/documents" replace />
            </ProtectedRoute>
          } />

          {/* Catch all - redirect to login */}
          <Route path="*" element={<Navigate to="/login" replace />} />
        </Routes>
      </Suspense>
    </div>
  );
}

function App() {
  useEffect(() => {
    // Initialize fetch interceptor before any API calls
    fetchInterceptor.init();
  }, []);

  return (
    <Router>
      <AuthProvider>
        <AppContent />
      </AuthProvider>
    </Router>
  );
}

export default App;